import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import Consent from 'components/Additional/Consent/Consent';
import CustomSelect from 'components/Additional/CustomSelect/CustomSelect';
import CustomInput from 'components/Additional/CustomInput/CustomInput';

import {
	validateDateFormat,
	validateAge,
	emailValidator,
	phoneValidator,
} from 'components/Additional/YourInfoForm/Validators';

import styles from './YourInfoFormContent.module.css';

const YourInfoFormContent = () => {
	const {
		control,
		register,
		setValue,
		watch,
		formState: { errors },
	} = useFormContext();

	const purchaseDate = watch('purchaseDate');
	const consentAccepted = watch('consentAccepted');

	const contactTimeOptions = [
		{ name: 'Morning', value: 'Morning' },
		{ name: 'Afternoon', value: 'Afternoon' },
		{ name: 'Evening', value: 'Evening' },
		{ name: 'Anytime', value: 'Anytime' },
	];

	const formatDate = (value) => {
		if (!value) return;

		const digitsOnly = value.replace(/\D/g, '');

		if (digitsOnly.length <= 2) {
			return digitsOnly;
		}

		if (digitsOnly.length <= 4) {
			return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
		}

		return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(
			2,
			4,
		)}/${digitsOnly.slice(4, 8)}`;
	};

	return (
		<div className={styles.your_info_form}>
			<div className={styles.content_box}>
				<p className={`${styles.section_1} ${styles.base_label}`}>
					Your name
				</p>

				<div className={styles.section_2_a}>
					<Controller
						name="first"
						control={control}
						defaultValue=""
						rules={{ required: 'First name is required' }}
						render={({ value, onChange }) => (
							<input
								className={styles.base_input}
								placeholder="First"
								value={value}
								onChange={onChange}
							/>
						)}
					/>
					<span className={styles.base_error_text}>
						{errors.first?.message}
					</span>
				</div>

				<div className={styles.section_2_b}>
					<Controller
						name="mi"
						control={control}
						defaultValue=""
						render={({ value, onChange }) => (
							<input
								className={styles.base_input}
								placeholder="MI"
								value={value}
								onChange={onChange}
							/>
						)}
					/>
					<span className={styles.base_error_text}>
						{errors.mi?.message}
					</span>
				</div>

				<div className={styles.section_2_c}>
					<Controller
						name="last"
						control={control}
						defaultValue=""
						rules={{ required: 'Last name is required' }}
						render={({ value, onChange }) => (
							<input
								className={styles.base_input}
								placeholder="Last"
								value={value}
								onChange={onChange}
							/>
						)}
					/>
					<span className={styles.base_error_text}>
						{errors.last?.message}
					</span>
				</div>

				<div className={styles.section_3_a}>
					<p className={styles.base_label}>Email</p>
					<Controller
						name="email"
						control={control}
						defaultValue=""
						rules={{
							required: 'Email is required',
							pattern: emailValidator,
						}}
						render={({ value, onChange }) => (
							<input
								className={styles.base_input}
								placeholder="Email"
								value={value}
								onChange={onChange}
							/>
						)}
					/>
					<span className={styles.base_error_text}>
						{errors.email?.message}
					</span>
				</div>

				<div className={styles.section_3_b}>
					<p className={styles.base_label}>Phone</p>
					<Controller
						name="phone"
						control={control}
						rules={{
							required: 'Phone is required',
							pattern: phoneValidator,
						}}
						render={({ value, onChange }) => (
							<NumberFormat
								value={value}
								onChange={onChange}
								format="(###) ###-####"
								mask="_"
								className={styles.base_input}
								placeholder="Phone"
							/>
						)}
					/>
					<span className={styles.base_error_text}>
						{errors.phone?.message}
					</span>
				</div>

				<div className={styles.section_3_c}>
					<p className={styles.base_label}>Birth date</p>
					<Controller
						name="birth"
						control={control}
						rules={{
							required: 'Birth date is required',
							validate: {
								format: validateDateFormat,
								age: validateAge,
							},
						}}
						render={({ value, onChange }) => (
							<input
								className={styles.base_input}
								maxLength="10"
								placeholder="MM/DD/YYYY"
								value={value}
								onChange={(e) =>
									onChange(formatDate(e.target.value))
								}
							/>
						)}
					/>

					<span className={styles.base_error_text}>
						{errors.birth?.message}
					</span>
				</div>

				<div className={`${styles.section_4_a} mt-2`}>
					<p className={`${styles.base_label} mr-3`}>
						What is the best time of day to contact you?
					</p>
				</div>

				<div className={styles.section_4_b}>
					<CustomSelect
						name="contactTime"
						layout="vertical"
						options={contactTimeOptions}
						ref={register({
							validate: (value) =>
								value !== '0' || 'Contact Time is required',
						})}
						error={errors.contactTime?.message}
					/>
				</div>
				{purchaseDate !== undefined && (
					<>
						<div className={`${styles.section_5_a} mt-2`}>
							<p className={styles.base_label}>
								Approximate Date of your home purchase?
							</p>
						</div>

						<div className={styles.section_5_b}>
							<CustomInput
								inputName="purchaseDate"
								placeholder="MM/DD/YYYY"
								error={errors.purchaseDate?.message}
								onInput={(e) => {
									const formatted = formatDate(
										e.target.value,
									);
									setValue('purchaseDate', formatted, {
										shouldDirty: true,
										shouldValidate: true,
									});
								}}
								ref={register({
									required: 'Purchase Date is required',
									validate: validateDateFormat,
								})}
							/>
							{/* <Controller
								name="purchaseDate"
								control={control}
								defaultValue=""
								rules={{
									required: 'Purchase Date is required',
									validate: validateDateFormat,
								}}
								render={({ value, onChange }) => (
									<input
										className={styles.base_input}
										maxLength="10"
										placeholder="MM/DD/YYYY"
										value={value}
										onChange={(e) =>
											onChange(formatDate(e.target.value))
										}
									/>
								)}
							/> */}
							{/* <span className={styles.base_error_text}>
								{errors.purchaseDate?.message}
							</span> */}
						</div>
					</>
				)}

				<Controller
					name="consentAccepted"
					control={control}
					render={({ value, onChange }) => (
						<Consent
							customClass={styles.section_12_a}
							value={value}
							onChange={onChange}
						/>
					)}
				/>

				<div className={styles.section_13_a}>
					<div className={styles.next_button_container}>
						<button
							type="submit"
							className={styles.next_button}
							disabled={!consentAccepted}
						>
							Submit
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default YourInfoFormContent;
